/* W11 — P-1 Obligation/Owner : qui doit quoi, avant quand, sous quelle condition,
 * avec quelle conséquence. Partagé Deal (exécution transactionnelle) & Governance (prochains pas). */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { ClipboardCheck, Plus, CheckCircle2, XCircle, AlertTriangle, Loader2, Lock } from "lucide-react";
import DateFieldFr from "@/components/DateFieldFr";

const KIND_LABEL = {
  condition_precedent: "Condition suspensive", approval: "Approbation",
  required_document: "Document requis", obligation: "Obligation", action: "Action",
};
const STATUS_META = {
  open: { label: "Ouverte", cls: "border-[#EF9F27]/40 text-[#EF9F27]" },
  resolved: { label: "Résolue", cls: "border-[#1D9E75]/40 text-[#1D9E75]" },
  approved: { label: "Approuvée", cls: "border-[#1D9E75]/40 text-[#1D9E75]" },
  rejected: { label: "Refusée", cls: "border-[#E24B4A]/40 text-[#E24B4A]" },
  cancelled: { label: "Annulée", cls: "border-white/15 text-zinc-500" },
};

function ObligationRow({ o, onReload }) {
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = STATUS_META[o.status] || STATUS_META.open;
  const overdue = o.status === "open" && o.due_at && o.due_at < new Date().toISOString();
  const resolve = async (outcome) => {
    if (!note.trim()) return notify.error("Preuve de résolution requise (note obligatoire).");
    setBusy(true);
    try {
      await api.post(`/workspace/obligations/${o.id}/resolve`,
        { note: note.trim(), evidence: evidence.trim() || null, outcome });
      notify.success("Résolution enregistrée avec preuve.");
      onReload();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  return (
    <div className={`p-2.5 rounded border ${overdue ? "border-[#E24B4A]/40 bg-[#E24B4A]/5" : "border-white/5 bg-white/[0.02]"}`}
      data-testid={`obligation-row-${o.id}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-400">{KIND_LABEL[o.kind] || o.kind}</span>
        {o.blocking && <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-[#E24B4A]/40 text-[#E24B4A] flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> bloquante</span>}
        <span className="text-zinc-200 flex-1 min-w-[160px]">{o.title}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${meta.cls}`}>{overdue ? "EN RETARD" : meta.label}</span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-1 flex gap-3 flex-wrap">
        {o.owner_email && <span>Responsable : {o.owner_email}</span>}
        {o.due_at && <span className={overdue ? "text-[#E24B4A]" : ""}>Avant le {new Date(o.due_at).toLocaleDateString("fr-CH")}</span>}
        {o.condition && <span>Condition : {o.condition}</span>}
        {o.consequence && <span className="text-[#EF9F27]">Si non fait : {o.consequence}</span>}
      </div>
      {o.description && <div className="text-[10px] text-zinc-500 mt-1">{o.description}</div>}
      {o.resolution && (
        <div className="text-[10px] text-zinc-400 mt-1 border-l-2 border-[#1D9E75]/40 pl-2" data-testid={`obligation-resolution-${o.id}`}>
          Preuve : {o.resolution.note}{o.resolution.evidence ? ` · ${o.resolution.evidence}` : ""} — {o.resolution.by_email}, {new Date(o.resolution.at).toLocaleDateString("fr-CH")}
        </div>
      )}
      {o.status === "open" && (
        resolving ? (
          <div className="flex gap-1.5 mt-2 flex-wrap items-center">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Preuve de résolution *" className="noam-input text-[10px] flex-1 min-w-[180px]" data-testid={`obligation-note-${o.id}`} />
            <input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="Référence (doc, lien…)" className="noam-input text-[10px] w-40" />
            {o.kind === "approval" ? (
              <>
                <button onClick={() => resolve("approved")} disabled={busy} className="btn-primary text-[10px]" data-testid={`obligation-approve-${o.id}`}><CheckCircle2 className="w-3 h-3" /> Approuver</button>
                <button onClick={() => resolve("rejected")} disabled={busy} className="btn-ghost text-[10px] text-[#E24B4A] border border-[#E24B4A]/30" data-testid={`obligation-reject-${o.id}`}><XCircle className="w-3 h-3" /> Refuser</button>
              </>
            ) : (
              <button onClick={() => resolve(null)} disabled={busy} className="btn-primary text-[10px]" data-testid={`obligation-resolve-confirm-${o.id}`}><CheckCircle2 className="w-3 h-3" /> Marquer résolue</button>
            )}
          </div>
        ) : (
          <button onClick={() => setResolving(true)} className="btn-ghost text-[10px] border border-white/10 mt-2" data-testid={`obligation-resolve-${o.id}`}>
            <CheckCircle2 className="w-3 h-3" /> Résoudre (preuve requise)
          </button>
        )
      )}
    </div>
  );
}

function CreateForm({ system, refType, refId, refLabel, onDone }) {
  const [f, setF] = useState({ kind: "obligation", title: "", owner_email: "", due_at: "", consequence: "", blocking: false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) return notify.error("Titre requis.");
    setBusy(true);
    try {
      await api.post("/workspace/obligations", {
        kind: f.kind, title: f.title.trim(),
        ref: { system, ref_type: refType, ref_id: refId, label: refLabel || null },
        owner_email: f.owner_email.trim() || null,
        due_at: f.due_at ? new Date(f.due_at).toISOString() : null,
        consequence: f.consequence.trim() || null,
        blocking: f.blocking,
      });
      notify.success("Engagement créé.");
      setF({ kind: "obligation", title: "", owner_email: "", due_at: "", consequence: "", blocking: false });
      onDone();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  return (
    <div className="p-2.5 rounded border border-dashed border-white/10 space-y-1.5" data-testid="obligation-create-form">
      <div className="flex gap-1.5 flex-wrap">
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600">Type
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="noam-input text-[10px] w-44" data-testid="obligation-kind-select">
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600 flex-1 min-w-[200px]">Engagement
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Qui doit quoi ? *" className="noam-input text-[10px] w-full" data-testid="obligation-title-input" />
        </label>
      </div>
      <div className="flex gap-1.5 flex-wrap items-end">
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600">Responsable
          <input value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} placeholder="email" className="noam-input text-[10px] w-52" data-testid="obligation-owner-input" />
        </label>
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600">Échéance
          <span className="flex items-center gap-1">
            <DateFieldFr value={f.due_at} onChange={(v) => setF({ ...f, due_at: v })} className="text-[10px] w-36" testid="obligation-due-input" />
            {f.due_at && <span className="text-[10px] text-zinc-400 normal-case tracking-normal">→ {new Date(f.due_at).toLocaleDateString("fr-CH")}</span>}
          </span>
        </label>
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600 flex-1 min-w-[160px]">Conséquence si non fait
          <input value={f.consequence} onChange={(e) => setF({ ...f, consequence: e.target.value })} placeholder="ex. closing impossible" className="noam-input text-[10px] w-full" />
        </label>
        {(f.kind === "condition_precedent" || f.kind === "approval" || f.kind === "required_document") && (
          <label className="flex items-center gap-1 text-[10px] text-zinc-400 pb-1.5">
            <input type="checkbox" checked={f.blocking} onChange={(e) => setF({ ...f, blocking: e.target.checked })} data-testid="obligation-blocking-check" />
            bloque la phase
          </label>
        )}
        <button onClick={submit} disabled={busy} className="btn-primary text-[10px]" data-testid="obligation-create-btn"><Plus className="w-3 h-3" /> Créer</button>
      </div>
    </div>
  );
}

export function ObligationsPanel({ system, refType, refId, refLabel, title = "Obligations & engagements", checklist = false }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/workspace/obligations?system=${system}&ref_id=${refId}`)
      .then((r) => setData(r.data)).catch((e) => notify.error(fmtError(e)));
  }, [system, refId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-6 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  const items = data.items || [];
  const open = items.filter((o) => o.status === "open");
  const done = items.length - open.length - items.filter((o) => o.status === "cancelled").length;
  const total = items.filter((o) => o.status !== "cancelled").length;
  return (
    <div className="space-y-2" data-testid="obligations-panel">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4 text-[#378ADD]" />
        <span className="text-xs font-semibold text-zinc-200 flex-1">{title}</span>
        {checklist && total > 0 && (
          <span className="text-[10px] text-zinc-400" data-testid="checklist-progress">
            {done}/{total} levée(s){data.blocking_open > 0 && <span className="text-[#E24B4A]"> · {data.blocking_open} bloquante(s) ouverte(s)</span>}
          </span>
        )}
      </div>
      {checklist && total > 0 && (
        <div className="h-1 rounded bg-white/5"><div className="h-1 rounded bg-[#1D9E75] transition-all" style={{ width: `${(done / total) * 100}%` }} /></div>
      )}
      {data.blocking_open > 0 && checklist && (
        <div className="text-[10px] text-[#E24B4A] flex items-center gap-1" data-testid="checklist-blocking-warning">
          <AlertTriangle className="w-3 h-3" /> La transition de phase est conditionnée : {data.blocking_open} condition(s) bloquante(s) doivent être levées avec preuve.
        </div>
      )}
      {items.length === 0 && (
        <div className="p-4 text-center text-[10px] text-zinc-600 border border-dashed border-white/10 rounded" data-testid="obligations-empty">
          Aucun engagement suivi. Sans suivi, ce qui doit arriver vit dans les emails et la mémoire humaine.
        </div>
      )}
      {items.map((o) => <ObligationRow key={o.id} o={o} onReload={load} />)}
      <CreateForm system={system} refType={refType} refId={refId} refLabel={refLabel} onDone={load} />
    </div>
  );
}
